import { Ativo, Device, Leitura, buscarAtivos, buscarDevices, buscarLeituras } from "../api/m5";
import {
  MapaDispositivos,
  agruparPorSerie,
  agruparPorTimestamp,
  criarMapaDispositivos,
  extrairTagChave,
} from "../components/agrupamentoSeries";
import { ValoresSeletor, montarSeletorDados } from "../components/seletorDados";
import { renderLineChart, SerieConfig } from "../components/charts/lineChart";
import { exportarCsv } from "../components/exportarCsv";
import { conectarWebSocket } from "../api/client";

type Eixo = "principal" | "secundario";

export async function montarGrafico(container: HTMLElement): Promise<void> {
  container.innerHTML = `
    <div id="filtros-grafico"></div>
    <div class="card">
      <div class="filtros" style="margin-bottom: 12px;">
        <button class="botao secundario" id="grafico-selecionar">Selecionar séries</button>
        <button class="botao secundario" id="grafico-exportar">Exportar CSV</button>
      </div>
      <div id="grafico-canvas" style="height: 420px;"></div>
    </div>
    <div id="grafico-modal-series"></div>
  `;

  const [ativos, devices]: [Ativo[], Device[]] = await Promise.all([
    buscarAtivos().catch(() => []),
    buscarDevices().catch(() => []),
  ]);
  const mapaDispositivos: MapaDispositivos = criarMapaDispositivos(devices, ativos);

  let leiturasAtuais: Leitura[] = [];
  let filtrosAtuais: ValoresSeletor = { assetIds: [], deviceIds: [], tags: [] };
  // Visibilidade por serie (chave completa de agruparPorSerie) e eixo por
  // VARIAVEL (tag_indice, sem prefixo de estacao/device) - marcar TA como
  // eixo secundario vale pra TA de todas as estacoes de uma vez, nao serie
  // por serie. Os dois sobrevivem a repaints (WS, reaplicar filtro) pra nao
  // perder a escolha do usuario a cada atualizacao.
  const serieAtiva: Record<string, boolean> = {};
  const eixoPorTag: Record<string, Eixo> = {};

  function sincronizarConfig(chaves: string[]): void {
    for (const chave of chaves) {
      if (!(chave in serieAtiva)) serieAtiva[chave] = true;
      const tagChave = extrairTagChave(chave);
      if (!(tagChave in eixoPorTag)) eixoPorTag[tagChave] = "principal";
    }
  }

  function repintar(): void {
    const el = container.querySelector("#grafico-canvas") as HTMLElement;
    el.innerHTML = "";
    if (leiturasAtuais.length === 0) {
      el.textContent = "Sem dados para o filtro selecionado.";
      return;
    }
    const todasSeries = agruparPorSerie(leiturasAtuais, mapaDispositivos);
    sincronizarConfig(Object.keys(todasSeries));

    const seriesVisiveis: Record<string, { timestamp: string; valor: number }[]> = {};
    const configVisivel: Record<string, SerieConfig> = {};
    for (const [chave, pontos] of Object.entries(todasSeries)) {
      if (!serieAtiva[chave]) continue;
      seriesVisiveis[chave] = pontos;
      configVisivel[chave] = { eixo: eixoPorTag[extrairTagChave(chave)] };
    }

    if (Object.keys(seriesVisiveis).length === 0) {
      el.textContent = "Nenhuma série selecionada para exibição — use \"Selecionar séries\".";
      return;
    }
    renderLineChart(el, seriesVisiveis, configVisivel);
  }

  async function carregar(filtros: ValoresSeletor) {
    filtrosAtuais = filtros;
    leiturasAtuais = await buscarLeituras({ ...filtros, limit: 5000, agregacao: "auto" });
    repintar();
  }

  container.querySelector("#grafico-exportar")!.addEventListener("click", () => {
    // Mesma estrutura pivotada (estacao + timestamp + colunas por variavel)
    // usada pela Tabela, nao as leituras cruas - mesmo dado, mesmo formato
    // de exportacao nas duas telas.
    const linhas = agruparPorTimestamp(leiturasAtuais, mapaDispositivos);
    const colunas = [...new Set(linhas.flatMap((l) => Object.keys(l.valores)))].sort();
    const linhasCsv = linhas.map((l) => {
      const linha: Record<string, unknown> = { Estação: l.estacao, Timestamp: l.timestamp };
      for (const c of colunas) linha[c] = l.valores[c] ?? "";
      return linha;
    });
    exportarCsv("grafico.csv", linhasCsv);
  });

  function fecharModalSeries(): void {
    const modalEl = container.querySelector("#grafico-modal-series") as HTMLElement;
    modalEl.innerHTML = "";
  }

  function abrirModalSeries(): void {
    const chaves = Object.keys(agruparPorSerie(leiturasAtuais, mapaDispositivos));
    sincronizarConfig(chaves);
    const modalEl = container.querySelector("#grafico-modal-series") as HTMLElement;

    if (chaves.length === 0) {
      modalEl.innerHTML = `
        <div class="modal-fundo">
          <div class="modal" style="width: 420px;">
            <h3>Selecionar séries exibidas</h3>
            <p class="aviso">Nenhuma série carregada ainda — ajuste os filtros primeiro.</p>
            <div class="modal-secao" style="display:flex; justify-content:flex-end;">
              <button class="botao secundario" id="grafico-selecao-fechar">Fechar</button>
            </div>
          </div>
        </div>`;
      modalEl.querySelector("#grafico-selecao-fechar")!.addEventListener("click", fecharModalSeries);
      return;
    }

    const tagsUnicas = [...new Set(chaves.map(extrairTagChave))].sort();

    modalEl.innerHTML = `
      <div class="modal-fundo">
        <div class="modal" style="width: 680px;">
          <h3>Selecionar séries exibidas</h3>
          <p class="aviso">À esquerda, quais séries aparecem no gráfico. À direita, em qual eixo Y cada variável é plotada — vale pra todas as estações/dispositivos daquela variável de uma vez.</p>
          <div style="display:flex; gap:20px;">
            <div style="flex: 1.4; min-width: 0;">
              <strong>Séries visíveis</strong>
              <div class="checklist" style="max-height: 320px; overflow-y: auto; margin-top: 6px;">
                ${chaves
                  .map(
                    (chave) => `
                  <label style="display:flex; align-items:center; gap:6px;">
                    <input type="checkbox" data-serie-ativo="${chave}" ${serieAtiva[chave] ? "checked" : ""}>
                    ${chave}
                  </label>`
                  )
                  .join("")}
              </div>
            </div>
            <div style="flex: 1; min-width: 0;">
              <strong>Eixo por variável</strong>
              <div class="checklist" style="max-height: 320px; overflow-y: auto; margin-top: 6px;">
                ${tagsUnicas
                  .map(
                    (tagChave) => `
                  <div style="display:flex; align-items:center; justify-content:space-between; gap:8px;">
                    <span>${tagChave}</span>
                    <select data-tag-eixo="${tagChave}">
                      <option value="principal" ${eixoPorTag[tagChave] === "principal" ? "selected" : ""}>Principal</option>
                      <option value="secundario" ${eixoPorTag[tagChave] === "secundario" ? "selected" : ""}>Secundário</option>
                    </select>
                  </div>`
                  )
                  .join("")}
              </div>
            </div>
          </div>
          <div class="modal-secao" style="display:flex; gap:8px; justify-content:flex-end;">
            <button class="botao secundario" id="grafico-selecao-fechar">Fechar</button>
            <button class="botao" id="grafico-selecao-aplicar">Aplicar</button>
          </div>
        </div>
      </div>`;

    modalEl.querySelector("#grafico-selecao-fechar")!.addEventListener("click", fecharModalSeries);
    modalEl.querySelector("#grafico-selecao-aplicar")!.addEventListener("click", () => {
      for (const chave of chaves) {
        const checkbox = modalEl.querySelector<HTMLInputElement>(`[data-serie-ativo="${chave}"]`)!;
        serieAtiva[chave] = checkbox.checked;
      }
      for (const tagChave of tagsUnicas) {
        const select = modalEl.querySelector<HTMLSelectElement>(`[data-tag-eixo="${tagChave}"]`)!;
        eixoPorTag[tagChave] = select.value === "secundario" ? "secundario" : "principal";
      }
      fecharModalSeries();
      repintar();
    });
  }

  container.querySelector("#grafico-selecionar")!.addEventListener("click", abrirModalSeries);

  // Por padrao, ao abrir a pagina: TA (temperatura do ar) de todas as
  // estacoes - ligar tudo de uma vez (todas as variaveis de todas as
  // estacoes) deixava o grafico ilegivel com o volume de dados atual.
  await montarSeletorDados(container.querySelector("#filtros-grafico")!, { tagsPadrao: ["TA"] }, carregar);

  // Atualizacao em tempo real via WebSocket (M5) - so aceita a leitura nova
  // se ela bater com o filtro de estacao/device/tag/intervalo aplicado no
  // momento, pra nao "vazar" dado de fora do que o usuario escolheu ver.
  function leituraBateComFiltro(l: Leitura): boolean {
    if (filtrosAtuais.deviceIds.length > 0 && !filtrosAtuais.deviceIds.includes(l.device_id)) return false;
    if (filtrosAtuais.assetIds.length > 0) {
      const assetDoDevice = devices.find((d) => d.device_id === l.device_id)?.asset_id;
      if (!assetDoDevice || !filtrosAtuais.assetIds.includes(assetDoDevice)) return false;
    }
    if (filtrosAtuais.tags.length > 0 && !filtrosAtuais.tags.includes(l.tag)) return false;
    if (filtrosAtuais.inicio && l.timestamp_medicao < filtrosAtuais.inicio) return false;
    if (filtrosAtuais.fim && l.timestamp_medicao > filtrosAtuais.fim) return false;
    return true;
  }

  const ws = conectarWebSocket((msg) => {
    if (!container.isConnected) {
      ws.close();
      return;
    }
    if (msg.tipo === "leitura_nova") {
      const leitura = msg.dado as Leitura;
      if (leituraBateComFiltro(leitura)) {
        leiturasAtuais.push(leitura);
        repintar();
      }
    }
  });
}
